import type { AxiosResponse } from 'axios';
import axiosInstance from '../superadminAxiosInstance';

export abstract class BaseService<
	T,
	TIDKey extends string,
	TArrayKey extends string,
> {
	protected crudResponseObjectName: string;
	protected crudResponseArrayName: string;
	protected objectID: TIDKey;

	constructor(
		objectID: TIDKey,
		crudResponseObjectName: string,
		crudResponseArrayName: string,
	) {
		this.crudResponseObjectName = crudResponseObjectName;
		this.crudResponseArrayName = crudResponseArrayName;
		this.objectID = objectID;
	}

	async getAllResources(queryParams?: {
		[key: string]: string | undefined;
	}): Promise<TavoittajaApiArrayResponse<TArrayKey, T[]>> {
		const response: AxiosResponse<TavoittajaApiArrayResponse<TArrayKey, T[]>> =
			await axiosInstance.get(
				`/superAdmin/${this.crudResponseArrayName}`,
				{
					params: {
						...queryParams,
					},
				},
			);

		return response.data;
	}

	async getSingleResource(
		resourceID: number,
		queryParams?: { [key: string]: string },
	): Promise<T> {
		const response: AxiosResponse<TavoittajaApiResponse<T>> =
			await axiosInstance.get(
				`/superAdmin/${this.crudResponseArrayName}/${resourceID}`,
				{
					params: {
						...queryParams,
					},
				},
			);

		const responseObject = (response.data.data as Record<string, T>)?.[
			this.crudResponseObjectName
		];

		if (!responseObject) {
			throw new Error('Response did not contain valid data.');
		}

		return responseObject;
	}

	async deleteSingleResource(resourceID: number): Promise<void> {
		const data = {
			[`${this.crudResponseObjectName}ID`]: resourceID,
		};
		await axiosInstance.delete(
			`/superAdmin/${this.crudResponseArrayName}`,
			{
				data,
			},
		);
	}

	async create(data: Partial<T>): Promise<T> {
		const response = await axiosInstance.post(
			`/superAdmin/${this.crudResponseArrayName}`,
			data,
		);

		const responseObject = (response.data.data as Record<string, T>)?.[
			this.crudResponseObjectName
		];

		if (!responseObject) {
			throw new Error('Response did not contain valid data.');
		}

		return responseObject;
	}

	async update(data: Partial<T>): Promise<T> {
		const response: AxiosResponse<TavoittajaApiResponse<T>> =
			await axiosInstance.patch(
				`/superAdmin/${this.crudResponseArrayName}`,
				data,
			);

		const responseObject = (response.data.data as Record<string, T>)?.[
			this.crudResponseObjectName
		];

		if (!responseObject) {
			throw new Error('Response did not contain valid data.');
		}

		return responseObject;
	}

	async deleteMultipleResources(
		data: ({
			[key in TIDKey]: number;
		} & {
			delete: boolean;
		})[],
	): Promise<void> {
		await axiosInstance.post(
			`/superAdmin/${this.crudResponseArrayName}/batch`,
			data,
		);
	}

	async batch(data: Record<string, Partial<T>[]>): Promise<unknown> {
		const response = await axiosInstance.post(
			`/superAdmin/${this.crudResponseArrayName}/batch`,
			data,
		);

		return response.data;
	}

	async getMetaData(language: string) {
		const response: AxiosResponse<TavoittajaApiResponse<{ meta: MetaData }>> =
			await axiosInstance.get(
				`/superAdmin/${this.crudResponseArrayName}/meta?language=${language}`,
			);
		return response.data;
	}
}
